import { HeartFillIcon } from '@ifrc-go/icons';
import {
    Heading,
    PageContainer,
} from '@ifrc-go/ui';
import { useTranslation } from '@ifrc-go/ui/hooks';
import { resolveToComponent } from '@ifrc-go/ui/utils';
import { _cs } from '@togglecorp/fujs';

import Link from '#components/Link';
import { resolveUrl } from '#utils/resolveUrl';

import {
    api,
    appCommitHash,
    appPackageName,
    appRepositoryUrl,
    appVersion,
} from '../../config';

import i18n from './i18n.json';
import styles from './styles.module.css';

const date = new Date();
const year = date.getFullYear();

interface Props {
    className?: string;
}

function GlobalFooter(props: Props) {
    const {
        className,
    } = props;

    const strings = useTranslation(i18n);
    // TODO: Add appVersion
    const versionTag = `${appPackageName}@${appVersion}`;
    const versionUrl = `${appRepositoryUrl}/releases/tag/${versionTag}`;
    const copyrightText = resolveToComponent(
        strings.footerIFRC,
        {
            year,
            appVersion: (
                <Link
                    href={versionUrl}
                    title={appCommitHash}
                    external
                >
                    {appVersion}
                </Link>
            ),
        },
    );

    return (
        <PageContainer
            className={_cs(styles.footer, className)}
            contentClassName={styles.content}
            containerAs="footer"
        >
            <div className={styles.section}>
                <Heading>
                    {strings.footerAboutRCSelect}
                </Heading>
                <div className={styles.description}>
                    {strings.footerAboutRCSelectDesc}
                </div>
                <div className={styles.copyright}>
                    {copyrightText}
                </div>
                <div className={styles.americanRedCrossLink}>
                    {strings.footerCraftedWith}
                    <HeartFillIcon />
                    {strings.footerCraftedBy}
                    <Link
                        className={styles.footerLink}
                        href="https://www.redcross.org/about-us/our-work/international-services.html"
                        external
                    >
                        {strings.footerByAmericanRedCross}
                    </Link>
                </div>
            </div>
            <div className={styles.section}>
                <Heading>
                    {strings.globalConnectWithIFRC}
                </Heading>
                <div className={styles.subSection}>
                    <Link
                        className={styles.footerLink}
                        href="https://dev.azure.com/IFRC/RC_Select"
                        external
                    >
                        {strings.footerOpenSourceCode}
                    </Link>
                    <Link
                        className={styles.footerLink}
                        href={resolveUrl(api, '/admin/')}
                        external
                    >
                        {strings.footerAdminPortalLink}
                    </Link>
                </div>
            </div>
            <div className={styles.section}>
                <Heading>
                    {strings.footerConnectWithIFRC}
                </Heading>
                <div className={styles.subSection}>
                    <Link
                        className={styles.footerLink}
                        href="https://ifrc.org"
                        external
                    >
                        {strings.footerIFRCTitle}
                    </Link>
                    <Link
                        className={styles.footerLink}
                        href="https://go.ifrc.org"
                        external
                    >
                        {strings.footerIFRCGOTitle}
                    </Link>
                    <Link
                        className={styles.footerLink}
                        href="https://communities.ifrc.org/group/digital-transformation-community-0/timeline"
                        external
                    >
                        {strings.footerIFRCGOCommunitiesIFRC}
                    </Link>
                </div>
            </div>
            <div className={styles.section}>
                <Heading>
                    {strings.footerContactUs}
                </Heading>
                <Link
                    className={styles.contactButton}
                    href="mailto:RCSelect@maillist.ifrc.org"
                    external
                    variant="primary"
                >
                    RCSelect@maillist.ifrc.org
                </Link>
                <Link
                    to="contactUsForm"
                    variant="tertiary"
                >
                    {strings.footerFeedbackButton}
                </Link>
            </div>
        </PageContainer>
    );
}

export default GlobalFooter;
